/**
 * Empty and error states.
 *
 * This component matters more than it looks. "No trades yet", "the backtest
 * has not run" and "your filter excluded everything" are three different
 * facts, and a panel that renders all three as blank space is how somebody
 * concludes their strategy is broken when it simply has not run.
 *
 * So `description` is not decoration: it says *why* the panel is empty and,
 * where there is one, offers the action that would fill it.
 */

import { AlertTriangle, Inbox } from 'lucide-react';
import type { ReactNode } from 'react';

import { cn } from '@/lib/cn';

import type { IconComponent } from './types';

export interface EmptyStateProps {
  icon?: IconComponent;
  title: string;
  description?: ReactNode;
  action?: ReactNode;
  className?: string;
  /** `sm` inside a dock tab, `md` for a whole panel. */
  size?: 'sm' | 'md';
}

export function EmptyState({
  icon: Icon = Inbox,
  title,
  description,
  action,
  className,
  size = 'md',
}: EmptyStateProps) {
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center text-center',
        size === 'sm' ? 'gap-1.5 px-4 py-6' : 'gap-2 px-6 py-12',
        className,
      )}
    >
      <Icon size={size === 'sm' ? 20 : 26} className="text-lo" strokeWidth={1.5} aria-hidden />
      <p className={cn('font-medium text-hi', size === 'sm' ? 'text-[12px]' : 'text-[13px]')}>
        {title}
      </p>
      {description && (
        <p className="max-w-sm text-[11.5px] leading-relaxed text-lo">{description}</p>
      )}
      {action && <div className="mt-1.5">{action}</div>}
    </div>
  );
}

/**
 * Error state.
 *
 * Always surfaces the backend's own message and the request id. "Something
 * went wrong" is not a diagnosis, and the request id is the only thing that
 * makes the server log searchable after the fact.
 */
export interface ErrorStateProps {
  title?: string;
  message?: string;
  requestId?: string;
  action?: ReactNode;
  className?: string;
  size?: 'sm' | 'md';
}

export function ErrorState({
  title = 'Could not load this',
  message,
  requestId,
  action,
  className,
  size = 'md',
}: ErrorStateProps) {
  return (
    <div
      role="alert"
      className={cn(
        'flex flex-col items-center justify-center gap-2 text-center',
        size === 'sm' ? 'px-4 py-6' : 'px-6 py-12',
        className,
      )}
    >
      <AlertTriangle
        size={size === 'sm' ? 20 : 26}
        className="text-danger"
        strokeWidth={1.5}
        aria-hidden
      />
      <p className="text-[13px] font-medium text-hi">{title}</p>
      {message && <p className="max-w-md text-[11.5px] leading-relaxed text-mid">{message}</p>}
      {requestId && (
        <p className="num text-[10.5px] text-lo">
          request {requestId} — quote this when checking the server log
        </p>
      )}
      {action && <div className="mt-1.5">{action}</div>}
    </div>
  );
}
